import { WORKFLOW_TRIGGER_GROUPS, type WorkflowTrigger, type WorkflowTriggerGroup } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { EmptyState, Select } from "@sagot/ui";
import { useState, type ReactNode } from "react";
import { useAgentList } from "../shared/use-engage-data";
import { OwnWorkflowsUpgrade } from "./defaults/upgrade-prompts";
import { TriggerTable } from "./trigger-table";
import type { WorkflowOut } from "./types";

const W = "engage.workflows";
const GROUPS = Object.keys(WORKFLOW_TRIGGER_GROUPS) as WorkflowTriggerGroup[];
const GROUPED: ReadonlySet<string> = new Set(Object.values(WORKFLOW_TRIGGER_GROUPS).flat());
/** The group a trigger that no group lists (a copy of the Developer webhooks default, whose trigger the picker hides) is shown under. */
const UNGROUPED_UNDER: WorkflowTriggerGroup = "other";

/** The triggers of a group that have workflows, plus (in the last group) any trigger no group lists, so no workflow of the workspace's own goes missing. */
function triggersOf(group: WorkflowTriggerGroup, rows: readonly WorkflowOut[]): WorkflowTrigger[] {
  const listed: readonly WorkflowTrigger[] = WORKFLOW_TRIGGER_GROUPS[group];
  const present = listed.filter((trigger) => rows.some((row) => row.trigger === trigger));
  if (group !== UNGROUPED_UNDER) return present;
  const strays = [...new Set(rows.map((row) => row.trigger).filter((trigger) => !GROUPED.has(trigger)))] as WorkflowTrigger[];
  return [...present, ...strays];
}

interface OwnWorkflowsProps {
  /** The workspace's own workflows (never the defaults). */
  workflows: readonly WorkflowOut[];
  canPublish: boolean;
  /** The plan has no workflows of the workspace's own: the upgrade card stands in for the empty state. */
  planBlocked: boolean;
  /** The "New workflow" button, shown in the empty state (nothing when the member cannot create). */
  newButton: ReactNode;
}

/** "Your workflows": the workspace's own, grouped by trigger and ordered within a trigger; an upgrade card on a plan without them. */
export function OwnWorkflows({ workflows, canPublish, planBlocked, newButton }: OwnWorkflowsProps) {
  const { t } = useTranslation();
  const agents = useAgentList();
  const [agentId, setAgentId] = useState("");
  // A workflow with no agent chosen runs for every agent, so it belongs to each agent's view.
  const rows = agentId ? workflows.filter((row) => row.agent_ids.length === 0 || row.agent_ids.includes(agentId)) : workflows;
  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-bold">{t(`${W}.defaults.own.title`)}</h2>
          <p className="text-sm text-fg-muted">{t(`${W}.defaults.own.subtitle`)}</p>
        </div>
        {workflows.length > 0 ? (
          <Select className="h-10 w-auto" aria-label={t(`${W}.filters.agent`)} value={agentId} onChange={(e) => { setAgentId(e.target.value); }}>
            <option value="">{t(`${W}.filters.allAgents`)}</option>
            {(agents.data ?? []).map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
          </Select>
        ) : null}
      </div>
      {planBlocked ? <OwnWorkflowsUpgrade /> : null}
      {workflows.length > 0 && rows.length === 0 ? <p className="rounded-md bg-surface-2 p-4 text-sm text-fg-muted" data-testid="filter-empty">{t(`${W}.filters.none`)}</p> : null}
      {workflows.length === 0 && !planBlocked ? <EmptyState icon="workflow" title={t(`${W}.empty.title`)} description={t(`${W}.empty.body`)} action={newButton ?? undefined} /> : null}
      {GROUPS.map((group) => {
        const triggers = triggersOf(group, rows);
        if (triggers.length === 0) return null;
        return (
          <section key={group} className="card card-body space-y-4" aria-labelledby={`group-${group}`}>
            <div>
              <h2 id={`group-${group}`} className="text-base font-bold">{t(`${W}.groups.${group}`)}</h2>
              <p className="text-xs text-fg-subtle">{t(`${W}.groupNote`)}</p>
            </div>
            {triggers.map((trigger) => (
              <TriggerTable key={trigger} trigger={trigger} rows={rows.filter((r) => r.trigger === trigger)} canPublish={canPublish} />
            ))}
          </section>
        );
      })}
    </>
  );
}
